import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Eager imports keep setup simple; for large catalogs consider i18next-http-backend + lazy ns.
// Import translation files
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enOrders from './locales/en/orders.json';
import enCustomers from './locales/en/customers.json';
import enStaff from './locales/en/staff.json';
import enDashboard from './locales/en/dashboard.json';
import enInventory from './locales/en/inventory.json';
import enInvestments from './locales/en/investments.json';
import enSettings from './locales/en/settings.json';
import enPurchases from './locales/en/purchases.json';
import enSuppliers from './locales/en/suppliers.json';
import enCatalog from './locales/en/catalog.json';
import enUsers from './locales/en/users.json';
import enAlerts from './locales/en/alerts.json';
import enInvestor from './locales/en/investor.json';
import enReports from './locales/en/reports.json';
import enPortal from './locales/en/portal.json';
import enProduction from './locales/en/production.json';
import enErrors from './locales/en/errors.json';

import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frOrders from './locales/fr/orders.json';
import frCustomers from './locales/fr/customers.json';
import frStaff from './locales/fr/staff.json';
import frDashboard from './locales/fr/dashboard.json';
import frInventory from './locales/fr/inventory.json';
import frInvestments from './locales/fr/investments.json';
import frSettings from './locales/fr/settings.json';
import frPurchases from './locales/fr/purchases.json';
import frSuppliers from './locales/fr/suppliers.json';
import frCatalog from './locales/fr/catalog.json';
import frUsers from './locales/fr/users.json';
import frAlerts from './locales/fr/alerts.json';
import frInvestor from './locales/fr/investor.json';
import frReports from './locales/fr/reports.json';
import frPortal from './locales/fr/portal.json';
import frProduction from './locales/fr/production.json';
import frErrors from './locales/fr/errors.json';

import arCommon from './locales/ar/common.json';
import arAuth from './locales/ar/auth.json';
import arOrders from './locales/ar/orders.json';
import arCustomers from './locales/ar/customers.json';
import arStaff from './locales/ar/staff.json';
import arDashboard from './locales/ar/dashboard.json';
import arInventory from './locales/ar/inventory.json';
import arInvestments from './locales/ar/investments.json';
import arSettings from './locales/ar/settings.json';
import arPurchases from './locales/ar/purchases.json';
import arSuppliers from './locales/ar/suppliers.json';
import arCatalog from './locales/ar/catalog.json';
import arUsers from './locales/ar/users.json';
import arAlerts from './locales/ar/alerts.json';
import arInvestor from './locales/ar/investor.json';
import arReports from './locales/ar/reports.json';
import arPortal from './locales/ar/portal.json';
import arProduction from './locales/ar/production.json';
import arErrors from './locales/ar/errors.json';

export const SUPPORTED_LOCALES = ['en', 'fr', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const RTL_LOCALES: SupportedLocale[] = ['ar'];

function updateDirection(lng: string) {
  const dir = RTL_LOCALES.includes(lng as SupportedLocale) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        orders: enOrders,
        customers: enCustomers,
        staff: enStaff,
        dashboard: enDashboard,
        inventory: enInventory,
        investments: enInvestments,
        settings: enSettings,
        purchases: enPurchases,
        suppliers: enSuppliers,
        catalog: enCatalog,
        users: enUsers,
        alerts: enAlerts,
        investor: enInvestor,
        reports: enReports,
        portal: enPortal,
        production: enProduction,
        errors: enErrors,
      },
      fr: {
        common: frCommon,
        auth: frAuth,
        orders: frOrders,
        customers: frCustomers,
        staff: frStaff,
        dashboard: frDashboard,
        inventory: frInventory,
        investments: frInvestments,
        settings: frSettings,
        purchases: frPurchases,
        suppliers: frSuppliers,
        catalog: frCatalog,
        users: frUsers,
        alerts: frAlerts,
        investor: frInvestor,
        reports: frReports,
        portal: frPortal,
        production: frProduction,
        errors: frErrors,
      },
      ar: {
        common: arCommon,
        auth: arAuth,
        orders: arOrders,
        customers: arCustomers,
        staff: arStaff,
        dashboard: arDashboard,
        inventory: arInventory,
        investments: arInvestments,
        settings: arSettings,
        purchases: arPurchases,
        suppliers: arSuppliers,
        catalog: arCatalog,
        users: arUsers,
        alerts: arAlerts,
        investor: arInvestor,
        reports: arReports,
        portal: arPortal,
        production: arProduction,
        errors: arErrors,
      },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    defaultNS: 'common',
    ns: ['common', 'auth', 'orders', 'customers', 'staff', 'dashboard', 'inventory', 'investments', 'settings', 'purchases', 'suppliers', 'catalog', 'users', 'alerts', 'investor', 'reports', 'portal', 'production', 'errors'],
    returnEmptyString: false,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
  });

// Set initial direction
updateDirection(i18n.language);

// Update direction on language change
i18n.on('languageChanged', updateDirection);

export default i18n;
